// Avisos de la cotización. ADENDA z48-5 (Diego, 28-oct): arriba solo lo que detiene el PDF, con «Ir a arreglarlo»; lo demás,
// agrupado y cerrado en «Revisar (N)». La nota nunca es obligatoria (junta del 13-oct).
import { useState } from 'react';
import { ChevronDown, CircleCheck, Info, OctagonAlert, Sparkles, TriangleAlert, Wrench } from 'lucide-react';
import { CODIGOS_QUE_DETIENEN_EL_PDF, campoDelRojoQueDetiene, MAXIMO_AVISOS_A_LA_VISTA, TEXTO_BOTON_AUMENTO, elegirALaVista, esAvisoAumentoPendiente, esAvisoImportante, esAvisoLimiteLey, fechaLarga, juntarAvisos, rojosPendientes } from '@cotizador/compartido';
import { sinPedirOk } from '../logica/sin-pedir-ok.ts';
import type { Aviso, AvisoALaVista, OrigenAviso } from '@cotizador/compartido';
import { ETIQUETA_OPCION } from '../logica/etiquetas.ts';
import { Boton, CampoArea, ErrorEnLinea } from './ui.tsx';

export const ETIQUETA_ORIGEN: Readonly<Record<OrigenAviso, string>> = {
  // Lo leído de los recibos (sin IA o con la IA): la mayoría de los PDF de CFE ya no pasan por la IA.
  ia_lectura: 'Lectura de recibos',
  ia_revision: 'IA · rentabilidad',
  motor: 'Cálculo',
  pdf: 'PDF',
  sistema: 'Sistema',
};

function Icono({ nivel }: { nivel: Aviso['nivel'] }) {
  if (nivel === 'rojo') return <OctagonAlert className="aviso__icono" aria-hidden />;
  if (nivel === 'amarillo') return <TriangleAlert className="aviso__icono" aria-hidden />;
  return <Info className="aviso__icono" aria-hidden />;
}

/** Cómo se atiende un aviso rojo: con una nota, con el OK de Diego (ADENDA A0b: precio) o arreglando el dato. */
export type ComoSeResuelve = 'nota' | 'ok_diego' | 'arreglar';

function UnAviso({
  aviso,
  alResolver,
  alIr,
  como = 'nota',
}: {
  aviso: Aviso;
  alResolver?: (a: Aviso, nota: string) => Promise<void>;
  alIr?: (campo: string) => void;
  como?: ComoSeResuelve;
}) {
  const [abierto, setAbierto] = useState(false);
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const confirmar = async () => {
    setEnviando(true);
    setError(null);
    try {
      // Diego, junta del 13-oct: la nota nunca es obligatoria («Entendido» si viene vacía).
      await alResolver!(aviso, nota.trim().length >= 3 ? nota.trim() : 'Entendido');
      setAbierto(false);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  };
  const nivel = aviso.resuelto ? 'resuelto' : aviso.nivel;
  return (
    <li className={`aviso aviso--${nivel}`}>
      {aviso.resuelto ? <CircleCheck className="aviso__icono" aria-hidden /> : <Icono nivel={aviso.nivel} />}
      <div className="aviso__cuerpo">
        <p className="aviso__mensaje">{sinPedirOk(aviso.mensaje)}</p>
        {aviso.fuente && (
          // Etapa 3 (Diego, 12-oct): cada alerta de regulación o de sol con su fuente y la fecha en que se revisó
          <p className="aviso__fuente">
            Fuente:{' '}
            {aviso.fuente.url ? (
              <a href={aviso.fuente.url} target="_blank" rel="noreferrer">
                {aviso.fuente.texto}
              </a>
            ) : (
              aviso.fuente.texto
            )}{' '}
            · revisada el {fechaLarga(aviso.fuente.fecha)}
          </p>
        )}
        <p className="aviso__meta">
          <span>{ETIQUETA_ORIGEN[aviso.origen]}</span>
          {aviso.opcion && <span>Opción {aviso.opcion === 'arranque' && /^Mejor retorno[ :(]/u.test(aviso.mensaje.trim()) ? 'mejor retorno' : ETIQUETA_OPCION[aviso.opcion].toLowerCase()}</span>}
          {aviso.resuelto && aviso.nota && <span className="aviso__nota">Confirmado: “{aviso.nota}”</span>}
        </p>
        {!aviso.resuelto && (alIr || alResolver) && (
          <div className="aviso__acciones">
            {aviso.campo && alIr && (esAvisoAumentoPendiente(aviso) ? (
              // ADENDA z52-2 (z48-5): el aumento pendiente lleva su botón, que abre el cuestionario en «Aumento de consumo»
              <Boton chico variante="noche" icono={<Sparkles aria-hidden />} onClick={() => alIr(aviso.campo!)}>
                {TEXTO_BOTON_AUMENTO}
              </Boton>
            ) : (
              <button type="button" className="enlace-boton" onClick={() => alIr(aviso.campo!)}>
                Ver el dato
              </button>
            ))}
            {alResolver && aviso.nivel !== 'info' && !abierto && como === 'nota' && (
              <button type="button" className="enlace-boton" onClick={() => setAbierto(true)}>
                Entendido, agregar nota
              </button>
            )}
            {como === 'ok_diego' && aviso.nivel === 'rojo' && <span className="aviso__como">Sugerencia: el PDF sale igual y Diego recibe el aviso.</span>}
            {como === 'arreglar' && aviso.nivel === 'rojo' && <span className="aviso__como">Sugerencia: revisa el dato (el PDF sale igual).</span>}
          </div>
        )}
        {abierto && (
          <div className="aviso__resolver">
            <CampoArea
              etiqueta="Nota (opcional; queda en el historial)"
              rows={2}
              maxLength={500}
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej. El cliente acepta pagar la mufa aparte."
            />
            <ErrorEnLinea error={error} />
            <div className="fila-botones">
              <Boton chico variante="fantasma" onClick={() => setAbierto(false)}>
                Cancelar
              </Boton>
              <Boton chico variante="noche" cargando={enviando} onClick={() => void confirmar()}>
                Confirmar aviso
              </Boton>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

/** Un aviso importante a la vista: UNA línea corta (la primera oración; si sale en varias opciones, «Arranque y Holgada: …»).
 *  Al tocarlo se abre el texto completo de cada aviso que junta, con su fuente y sus acciones. */
function AvisoCorto({
  grupo,
  alResolver,
  alIr,
  comoSeResuelve,
}: {
  grupo: AvisoALaVista;
  alResolver?: (a: Aviso, nota: string) => Promise<void>;
  alIr?: (campo: string) => void;
  comoSeResuelve?: (a: Aviso) => ComoSeResuelve;
}) {
  const [abierto, setAbierto] = useState(false);
  // Verificación del 28-oct: «Calcular el aumento» a la vista, sin desplegar el aviso (antes: Revisar → desplegar → Calcular)
  const aumento = !abierto && alIr && grupo.avisos.length === 1 && !grupo.avisos[0]!.resuelto && esAvisoAumentoPendiente(grupo.avisos[0]!) ? grupo.avisos[0]! : null;
  return (
    <li className={`aviso aviso--${grupo.nivel} aviso--corto`}>
      <Icono nivel={grupo.nivel} />
      <div className="aviso__cuerpo">
        <button type="button" className="aviso__corto" aria-expanded={abierto} onClick={() => setAbierto((x) => !x)}>
          <span className="aviso__mensaje">{grupo.corto}</span>
          <ChevronDown aria-hidden className="aviso__flecha" />
        </button>
        {aumento && (
          <div className="aviso__acciones">
            <Boton chico variante="noche" icono={<Sparkles aria-hidden />} onClick={() => alIr!(aumento.campo!)}>
              {TEXTO_BOTON_AUMENTO}
            </Boton>
          </div>
        )}
        {abierto && (
          <ul className="lista-avisos lista-avisos--detalle">
            {grupo.avisos.map((a) => (
              <UnAviso key={a.id} aviso={a} alResolver={alResolver} alIr={alIr} como={comoSeResuelve?.(a)} />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

/** ADENDA z48-5 (Diego, 28-oct): ¿este aviso DETIENE el PDF? Los rojos de CODIGOS_QUE_DETIENEN_EL_PDF (viáticos, aislado sin
 *  equipos, ampliación incompleta…) y el límite de la ley (0.7 MW). La pantalla puede sumar los suyos (p. ej. el precio de
 *  Optimus que solo mantiene Diego, para quien no es Diego). */
export function detieneElPdf(a: Aviso): boolean {
  return !a.resuelto && a.nivel === 'rojo' && (CODIGOS_QUE_DETIENEN_EL_PDF.includes(a.codigo) || esAvisoLimiteLey(a));
}

/** Dónde se arregla un aviso que detiene el PDF: el campo del mapa (compartido → avisos-vista.ts), el del aviso o, en el de la ley,
 *  las opciones. null = no hay a dónde llevar (el texto lo dice). */
export function campoParaArreglar(a: Aviso): string | null {
  // hoy76: el del aislado con módulos fijados lleva a Opciones (campoDelRojoQueDetiene); los demás, al campo fijo de su código.
  return campoDelRojoQueDetiene(a) ?? a.campo ?? (esAvisoLimiteLey(a) ? '/parametros/opciones_activas' : null);
}

/** Un aviso que detiene el PDF: a la vista, completo, con «Ir a arreglarlo». */
function AvisoQueDetiene({ grupo, alIr }: { grupo: AvisoALaVista; alIr?: (campo: string) => void }) {
  const campo = campoParaArreglar(grupo.avisos[0]!);
  return (
    <li className="aviso aviso--rojo">
      <Icono nivel="rojo" />
      <div className="aviso__cuerpo">
        <p className="aviso__mensaje">{grupo.avisos.length === 1 ? sinPedirOk(grupo.avisos[0]!.mensaje) : grupo.corto}</p>
        {campo && alIr && (
          <div className="aviso__acciones">
            <Boton chico variante="noche" icono={<Wrench aria-hidden />} onClick={() => alIr(campo)}>
              Ir a arreglarlo
            </Boton>
          </div>
        )}
      </div>
    </li>
  );
}

/** Banda de avisos de la cotización. ADENDA z48-5 (Diego, 28-oct; MANDA sobre los «3 a la vista» del 13-oct): arriba SOLO lo que
 *  detiene el PDF (rojo, cada uno con «Ir a arreglarlo»); todo lo demás (amarillos, informativos y los rojos que son sugerencia)
 *  agrupado y CERRADO en «Revisar (N)», juntado sin repetidos y ordenado por importancia. Ningún aviso se pierde. Con `soloRojos`
 *  (quien solo ve: pestaña PDF), los rojos como antes. */
export function BandaAvisos({
  avisos,
  alResolver,
  alIr,
  soloRojos,
  comoSeResuelve,
  detiene = detieneElPdf,
}: {
  avisos: readonly Aviso[];
  alResolver?: (a: Aviso, nota: string) => Promise<void>;
  alIr?: (campo: string) => void;
  soloRojos?: boolean;
  /** Por aviso: nota (por omisión), OK de Diego (precio, para quien no es director) o arreglar el dato. */
  comoSeResuelve?: (a: Aviso) => ComoSeResuelve;
  /** Qué detiene el PDF (por omisión detieneElPdf). */
  detiene?: (a: Aviso) => boolean;
}) {
  if (soloRojos) return <BandaSoloRojos avisos={avisos} />;
  const arriba = juntarAvisos(avisos.filter((a) => !a.resuelto && detiene(a)));
  const resto = avisos.filter((a) => a.resuelto || !detiene(a));
  const pendientes = resto.filter((a) => !a.resuelto);
  const resueltos = resto.filter((a) => a.resuelto);
  // Primero lo importante (precio, recibo, ley: el orden del 13-oct) y después lo menor, lo informativo y lo de la IA.
  const grupos = [...juntarAvisos(pendientes.filter(esAvisoImportante)), ...juntarAvisos(pendientes.filter((a) => !esAvisoImportante(a)))];
  const rojos = pendientes.filter((a) => a.nivel === 'rojo').length;
  if (arriba.length === 0 && resto.length === 0) return null;
  return (
    <div className="banda-avisos" aria-label="Avisos de la cotización">
      {arriba.length > 0 && (
        <ul className="lista-avisos" aria-label="Detienen el PDF" aria-live="polite">
          {arriba.map((g) => (
            <AvisoQueDetiene key={g.llave} grupo={g} alIr={alIr} />
          ))}
        </ul>
      )}
      {resto.length > 0 && (
        <details className="plegable">
          <summary>
            <ChevronDown aria-hidden className="plegable__flecha" />
            <span>Revisar ({pendientes.length})</span>
            {rojos > 0 && <span className="texto-rojo"> · {rojos === 1 ? '1 en rojo' : `${rojos} en rojo`}</span>}
            {resueltos.length > 0 && <span className="nota-suave"> · {resueltos.length === 1 ? '1 confirmado' : `${resueltos.length} confirmados`}</span>}
          </summary>
          <ul className="lista-avisos" aria-label="Para revisar">
            {grupos.map((g) => (
              <AvisoCorto key={g.llave} grupo={g} alResolver={alResolver} alIr={alIr} comoSeResuelve={comoSeResuelve} />
            ))}
            {resueltos.map((a) => (
              <UnAviso key={a.id} aviso={a} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Quien solo ve (pestaña PDF): los rojos pendientes, máximo 3 a la vista y todos en «Ver todos (N)». */
function BandaSoloRojos({ avisos }: { avisos: readonly Aviso[] }) {
  const rojos = rojosPendientes(avisos).filter((a) => a.origen !== 'ia_revision');
  const { importantes, rojos_ocultos } = elegirALaVista(juntarAvisos(rojos), MAXIMO_AVISOS_A_LA_VISTA);
  if (importantes.length === 0 && rojos.length === 0) return null;
  return (
    <div className="banda-avisos" aria-label="Avisos de la cotización">
      {importantes.length > 0 && (
        <ul className="lista-avisos" aria-label="Lo importante" aria-live="polite">
          {importantes.map((g) => (
            <AvisoCorto key={g.llave} grupo={g} />
          ))}
        </ul>
      )}
      {rojos_ocultos > 0 && <p className="nota-suave texto-rojo">Y {rojos_ocultos === 1 ? 'otro aviso rojo' : `${rojos_ocultos} avisos rojos más`} en «Ver todos».</p>}
      {rojos.length > 0 && (
        <details className="plegable">
          <summary>
            <ChevronDown aria-hidden className="plegable__flecha" />
            Ver todos ({rojos.length})
          </summary>
          <ul className="lista-avisos">
            {rojos.map((a) => (
              <UnAviso key={a.id} aviso={a} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
